import { memo, useState } from 'react';
import { CARD_IMG_BASE } from './cards/cardUtils';
import { roundBook, fmtBook } from '../../utils/casinoBook';

function findByNat(sub, nat) {
  return sub.find((s) => s.nat === nat);
}

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}

function suspCls(item) {
  return isSusp(item) ? ' suspended' : '';
}

function fmtAmt(n) {
  const x = Number(n) || 0;
  if (x >= 100000) return `${x / 100000}L`;
  if (x >= 1000) return `${x / 1000}K`;
  return String(x);
}

function bookOf(exposures, name, sid) {
  return roundBook(exposures?.[`sid:${sid}`] ?? exposures?.[name] ?? 0);
}

function bookCls(v) {
  return v < 0 ? 'book-red' : v > 0 ? 'book-green' : 'book-black';
}

// dt1dayfancy range collapse (info + icon-range, float-left before label)
function FancyRange({ rangeId, min, max }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="float-left mr-2">
      <i
        data-toggle="collapse"
        data-target={`#${rangeId}`}
        className="fas fa-info-circle"
        onClick={() => setOpen((o) => !o)}
      ></i>
      {' '}
      <div id={rangeId} className={`collapse icon-range${open ? ' show' : ''}`}>
        R:
        <span>{fmtAmt(min)}</span>
        -
        <span>{fmtAmt(max)}</span>
      </div>
    </div>
  );
}

// static min-max line (float-right casino-min-max)
function MinMax({ min, max }) {
  return (
    <div className="casino-box-row casino-odds">
      <div className="text-center w-100">
        <span className="float-right casino-min-max">
          R:
          <span>{fmtAmt(min)}</span>
          -
          <span>{fmtAmt(max)}</span>
        </span>
      </div>
    </div>
  );
}

const DragonTigerDT6Side = memo(function DragonTigerDT6Side({
  label, labelClass, swapSuits,
  even, odd, black, red, spade, heart, club, diamond,
  onBet, exposures, suitLockTop,
}) {
  const bEven = bookOf(exposures, even?.nat, even?.sid);
  const bOdd = bookOf(exposures, odd?.nat, odd?.sid);
  const bBlack = bookOf(exposures, black?.nat, black?.sid);
  const bRed = bookOf(exposures, red?.nat, red?.sid);

  const blackBox = (
    <div className={`casino-bl-box${suspCls(black)}`} key="black">
      <div
        className="back casino-bl-box-item casino-card-img"
        onClick={isSusp(black) ? undefined : () => onBet(black.sid, 'back', black.b, black)}
      >
        <span>
          <img src={`${CARD_IMG_BASE}/spade.png`} />
          {' '}
          <img src={`${CARD_IMG_BASE}/club.png`} />
        </span>
        {' '}
        <span className={`casino-book ${bookCls(bBlack)}`}>{fmtBook(bBlack)}</span>
      </div>
    </div>
  );
  const redBox = (
    <div className={`casino-bl-box${suspCls(red)}`} key="red">
      <div
        className="back casino-bl-box-item casino-card-img"
        onClick={isSusp(red) ? undefined : () => onBet(red.sid, 'back', red.b, red)}
      >
        <span>
          <img src={`${CARD_IMG_BASE}/heart.png`} />
          {' '}
          <img src={`${CARD_IMG_BASE}/diamond.png`} />
        </span>
        {' '}
        <span className={`casino-book ${bookCls(bRed)}`}>{fmtBook(bRed)}</span>
      </div>
    </div>
  );

  // Single-suit market: with a book, a locked cell pins its padlock to the TOP
  // (style.css `.suspended.lock-top:before`) so the figure stays readable.
  // dt6 only (suitLockTop); pdt6 shares this table and is unchanged.
  const suitBet = (item, book) => (
    <div className="casino-bl-box">
      <div
        className={`back casino-bl-box-item${suitLockTop && book ? ' lock-top' : ''}${suspCls(item)}`}
        onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'back', item.b, item)}
      >
        <span className="casino-box-odd">{item?.b ?? 0}</span>
        {' '}
        <span className={`casino-book ${bookCls(book)}`}>{fmtBook(book)}</span>
      </div>
    </div>
  );

  const bSpade = bookOf(exposures, spade?.nat, spade?.sid);
  const bHeart = bookOf(exposures, heart?.nat, heart?.sid);
  const bClub = bookOf(exposures, club?.nat, club?.sid);
  const bDiamond = bookOf(exposures, diamond?.nat, diamond?.sid);

  return (
    <>
      <div>
        <div className="casino-box-row casino-odds casino-title">
          <div className="text-left w-100">
            <b className={labelClass}>{label}</b>
          </div>
        </div>

        <div className="casino-box-row">
          <div className="casino-bl-box"><b>{even?.b ?? 0}</b></div>
          <div className="casino-bl-box"><b>{odd?.b ?? 0}</b></div>
          <div className="casino-bl-box"><b>{black?.b ?? 0}</b></div>
          <div className="casino-bl-box"><b>{red?.b ?? 0}</b></div>
        </div>

        <div className="casino-box-row">
          <div className={`casino-bl-box${suspCls(even)}`}>
            <div
              className="back casino-bl-box-item"
              onClick={isSusp(even) ? undefined : () => onBet(even.sid, 'back', even.b, even)}
            >
              <span className="casino-box-odd">Even</span>
              {' '}
              <span className={`casino-book ${bookCls(bEven)}`}>{fmtBook(bEven)}</span>
            </div>
          </div>
          <div className={`casino-bl-box${suspCls(odd)}`}>
            <div
              className="back casino-bl-box-item"
              onClick={isSusp(odd) ? undefined : () => onBet(odd.sid, 'back', odd.b, odd)}
            >
              <span className="casino-box-odd">Odd</span>
              {' '}
              <span className={`casino-book ${bookCls(bOdd)}`}>{fmtBook(bOdd)}</span>
            </div>
          </div>
          {swapSuits ? [redBox, blackBox] : [blackBox, redBox]}
        </div>

        <MinMax min={even?.min ?? 100} max={even?.max ?? 25000} />
      </div>

      <div className="mt-3">
        <div className="casino-box-row">
          <div className="casino-bl-box">
            <div className="casino-bl-box-item casino-card-img"><img src={`${CARD_IMG_BASE}/spade.png`} /></div>
          </div>
          <div className="casino-bl-box">
            <div className="casino-bl-box-item casino-card-img"><img src={`${CARD_IMG_BASE}/heart.png`} /></div>
          </div>
          <div className="casino-bl-box">
            <div className="casino-bl-box-item casino-card-img"><img src={`${CARD_IMG_BASE}/club.png`} /></div>
          </div>
          <div className="casino-bl-box">
            <div className="casino-bl-box-item casino-card-img"><img src={`${CARD_IMG_BASE}/diamond.png`} /></div>
          </div>
        </div>
        <div className="casino-box-row">
          {suitBet(spade, bSpade)}
          {suitBet(heart, bHeart)}
          {suitBet(club, bClub)}
          {suitBet(diamond, bDiamond)}
        </div>
        <MinMax min={spade?.min ?? 100} max={spade?.max ?? 25000} />
      </div>
    </>
  );
});

// dt1dayfancy top row: Dragon/Tiger (back+lay) or Pair (back only)
function FancyRow({ cls, label, rangeId, item, hasLay, exposures, onBet }) {
  const book = bookOf(exposures, item?.nat, item?.sid);
  return (
    <div className={`casino-box-row ${cls}`}>
      <div className="casino-nation-name">
        {/* max from the feed (dt6 3L / 10K, pdt6 2L / 20K); old fixed values as fallback */}
        <FancyRange rangeId={rangeId} min={item?.min ?? 100} max={item?.max ?? (hasLay ? 300000 : 10000)} />
        {' '}
        <b>{label}</b>
      </div>
      <div className="casino-bl-box">
        <div
          className={`back casino-bl-box-item${suspCls(item)}`}
          onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'back', item.b, item)}
        >
          <span className="casino-box-odd">{item?.b ?? 0}</span>
        </div>
        {hasLay && (
          <div
            className={`lay casino-bl-box-item${suspCls(item)}`}
            onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'lay', item.l, item)}
          >
            <span className="casino-box-odd">{item?.l ?? 0}</span>
          </div>
        )}
      </div>
      <div className="casino-nation-name text-center w-100">
        <span className={`casino-book ${bookCls(book)}`}>{fmtBook(book)}</span>
      </div>
    </div>
  );
}

const BetTableDT6 = memo(function BetTableDT6({ gameData, onBet, exposures, suitLockTop = false }) {
  const sub = gameData?.sub || [];
  const handleBet = onBet || (() => {});

  const dragon = findByNat(sub, 'Dragon');
  const pair = findByNat(sub, 'Pair');
  const tiger = findByNat(sub, 'Tiger');

  const dragonEven = findByNat(sub, 'Dragon Even');
  const dragonOdd = findByNat(sub, 'Dragon Odd');
  const dragonBlack = findByNat(sub, 'Dragon Black');
  const dragonRed = findByNat(sub, 'Dragon Red');
  const dragonSpade = findByNat(sub, 'Dragon Spade');
  const dragonHeart = findByNat(sub, 'Dragon Heart');
  const dragonClub = findByNat(sub, 'Dragon Club');
  const dragonDiamond = findByNat(sub, 'Dragon Diamond');

  const tigerEven = findByNat(sub, 'Tiger Even');
  const tigerOdd = findByNat(sub, 'Tiger Odd');
  const tigerBlack = findByNat(sub, 'Tiger Black');
  const tigerRed = findByNat(sub, 'Tiger Red');
  const tigerSpade = findByNat(sub, 'Tiger Spade');
  const tigerHeart = findByNat(sub, 'Tiger Heart');
  const tigerClub = findByNat(sub, 'Tiger Club');
  const tigerDiamond = findByNat(sub, 'Tiger Diamond');

  return (
    <>
      <div className="dt1dayfancy">
        <FancyRow cls="dragonfancy" label="Dragon" rangeId="demo0" item={dragon} hasLay exposures={exposures} onBet={handleBet} />
        <FancyRow cls="pairfancy" label="Pair" rangeId="demo2" item={pair} hasLay={false} exposures={exposures} onBet={handleBet} />
        <FancyRow cls="tigerfancy" label="Tiger" rangeId="demo1" item={tiger} hasLay exposures={exposures} onBet={handleBet} />
      </div>

      <div className="teen1daycasino-container mt-2">
        <div className="teen1dayleft">
          <DragonTigerDT6Side
            exposures={exposures}
            label="Dragon"
            labelClass="text-playera"
            even={dragonEven}
            odd={dragonOdd}
            black={dragonBlack}
            red={dragonRed}
            spade={dragonSpade}
            heart={dragonHeart}
            club={dragonClub}
            diamond={dragonDiamond}
            onBet={handleBet}
            suitLockTop={suitLockTop}
          />
        </div>

        <div className="teen1daycenter"></div>

        <div className="teen1dayright">
          <DragonTigerDT6Side
            exposures={exposures}
            label="Tiger"
            labelClass="text-playerb"
            swapSuits
            even={tigerEven}
            odd={tigerOdd}
            black={tigerBlack}
            red={tigerRed}
            spade={tigerSpade}
            heart={tigerHeart}
            club={tigerClub}
            diamond={tigerDiamond}
            onBet={handleBet}
            suitLockTop={suitLockTop}
          />
        </div>
      </div>
    </>
  );
});

export default BetTableDT6;
